import React, { useState, useContext, useEffect } from 'react';
import { PieChart, pieArcLabelClasses } from '@mui/x-charts/PieChart';
import { CircularProgress, Stack, Box, useMediaQuery } from "@mui/material";
import { useTheme } from '@mui/material/styles';

import GamesContext from '../../store/games-context';
import RunsContext from '../../store/runs-context';

import Run, { StatRuns } from '../../types/run';
import Game from '../../types/game';

import GameTable from '../../components/Statistics/GameTable';
import FilterForm from '../../components/Statistics/FilterForm';
import StatTable from '../../components/Statistics/StatTable';
import GameRunModal from '../../components/Statistics/GameRunModal';
import StreakTable from '../../components/Statistics/StreakTable';

import { successRunFilter, calculateSuccessRate, groupByDateAcc, sortByDate, getRunsByGameNumber, sortRuns, stringDateToDate } from '../../utils/utils';

import styles from './statistics.module.scss';
import ds1Logo from '../../static/img/ds1.png';
import ds2Logo from '../../static/img/ds2.png';
import ds3Logo from '../../static/img/ds3.png';
import desLogo from '../../static/img/des.png';
import bbLogo from '../../static/img/bb.png';
import skLogo from '../../static/img/sk.png';
import erLogo from '../../static/img/er.png';
import Split from '../../types/split';
import SplitHitModal from '../../components/Statistics/SplitHitModal';

const logos = [ds1Logo, ds2Logo, ds3Logo, desLogo, bbLogo, skLogo, erLogo];


const getTotalRuns = (filteredRunsNoPartial: Run[], games: Game[]): Run[][] => {
  return games.map(game => filteredRunsNoPartial.filter(run => run.selectedGame === game.id));
};

const filterInitialDate = (runToFilter: Run, initialDate: string) => {
  if (runToFilter && runToFilter.date) {
    return (new Date(runToFilter.date).getTime() >= new Date(initialDate).getTime())
  }
  return false;
};
const filterEndDate = (runToFilter: Run, endDate: string) => {
  if (runToFilter && runToFilter.date) {
    return (new Date(runToFilter.date) <= new Date(endDate));
  }
  return false
};

const Statistics = () => {
  const gamesContext = useContext(GamesContext);
  const runsContext = useContext(RunsContext);

  const games = gamesContext.games;
  const gamesColors = gamesContext.gamesColors;
  const initialRuns = runsContext.runs;
    
  const isMobile = useMediaQuery(useTheme().breakpoints.down('md'));
  const [initialDate, setInitalDate] = useState('');
  const [endDate, setEndDate] = useState(null);
  const [splitHits, setSplitHits] = useState<Split[]>([] as Split[]);
  const [selectedGameRuns, setSelectedGameRuns] = useState<Run[]>([] as Run[]);
  const [runsFiltered, setRunsFiltered] = useState<Run[]>(initialRuns);
  const [currentSplitName, setCurrentSplitName] = useState('');
  const [currentGameName, setCurrentGameName] = useState('');
  const [currentFilterName, setCurrentFilterName] = useState<string>('');

  const setCurrentFilter = (runs: Run[], newFilterName: string) => {
    setRunsFiltered(runs);
    setCurrentFilterName(newFilterName);
  }

  useEffect(() => {
    setRunsFiltered(initialRuns);
  }, [initialRuns]);

  const runs = runsFiltered;

  if (games.length > 0 && runs.length > 0) {
    const groupedRuns = runs.reduce(groupByDateAcc, []);
    const sortedRuns = groupedRuns?.sort(sortByDate);
    if(sortedRuns && sortedRuns[0] && initialDate === '') {
      setInitalDate(sortedRuns[0].date);
    }

    // const gamesSplits = getGamesSplits(games);

    const initialRunsSortedByDate = initialRuns.sort(sortRuns);

    let filteredRuns = runs.filter(run => {
      if (run && run.date) {
        return filterInitialDate(run, initialDate);
      }
      return false;
    });
    let initialFilteredRuns = initialRunsSortedByDate.filter(run => {
      if (run && run.date) {
        return (new Date(run.date).getTime() >= new Date(initialDate).getTime())
      }
      return false;
    });
    if (endDate) {
      filteredRuns = filteredRuns.filter(run => filterEndDate(run, endDate));
      initialFilteredRuns = initialRunsSortedByDate.filter(run => filterEndDate(run, endDate));
    }

    const runsByGameNumber = getRunsByGameNumber({ runs: filteredRuns, initialRuns: initialFilteredRuns, games });


    const filteredRunsNoPartial = filteredRuns.filter(run => {
      return (run.endSplit === undefined || run.clip);
    });

    const initialFilteredRunsNoPartial = initialFilteredRuns.filter(run => {
      return (!run.partial  || run.includesHit || run.clip);
    });

    const totalRuns = getTotalRuns(filteredRunsNoPartial, games);

    const onSplitHitClick = (splitHits: Split[]) => {
      setSplitHits(splitHits);
      setCurrentSplitName(splitHits[0].name);
    };

    let gameStats = [
      {
        runs: filteredRunsNoPartial,
        displayText: 'All'
      }
    ];

    const initialGameStats = [
      {
        runs: initialFilteredRunsNoPartial,
        displayText: 'All'
      }
    ];

    games.forEach((game, index) => gameStats.push({
      runs: totalRuns[index],
      displayText: game.acronym
    }));

    let gameOrderStats = [
      {
        runs: filteredRunsNoPartial,
        displayText: 'All'
      }
    ];

    runsByGameNumber.forEach((runsForGameOrder, index) => gameOrderStats.push({
      runs: runsForGameOrder,
      displayText: `#${index+1}`
    }));

    const statRowItemClickHandler = (runs: Run[], text: string) => {
      setSelectedGameRuns(runs);
      setCurrentGameName(text);
    };

    let chatData: any[][] = [['Game', 'Value']];

    gameStats.forEach((gameStat, index) => {
      if (index === 0) {
        return;// remove All stats
      }
      const successfulRuns = gameStat.runs.filter(successRunFilter);
      chatData.push([gameStat.displayText, calculateSuccessRate({ successfulRuns, totalRuns: gameStat.runs })]);
    });

    const getPerGameData = (gameStats: StatRuns[]) => {
      const perGameData: any[] = [];
      gameStats.forEach((gameStat, index) => {
        if (index === 0) {
          return;// remove All stats
        }
        const succesfulGameRuns = gameStat.runs.filter(successRunFilter);
        perGameData.push({ label: gameStat.displayText, value: succesfulGameRuns.length });
      });
      return perGameData;
    }

    const getPerGameOrderData = (gameOrderStats: StatRuns[]) => {
      const perGameData: any[] = [];
      gameOrderStats.forEach((gameNumberStat, index) => {
        if (index === 0) {
          return;// remove All stats
        }
        const succesfulGameRuns = gameNumberStat.runs.filter(successRunFilter);
        perGameData.push({ label: gameNumberStat.displayText, value: succesfulGameRuns.length });
      });
      return perGameData;
    }

    const perGameData = getPerGameData(gameStats);
    const perGameOrderData = getPerGameOrderData(gameOrderStats);

    return (
      <>
        <div
          className={styles.statistics}
        >
          <Stack
            component="div"
            sx={{ margin: '0 auto', maxWidth: 'xl' }}
            className={styles.splitStats}
            direction='column'
          >
            <FilterForm setInitalDate={setInitalDate} setEndDate={setEndDate} initialDate={initialDate} endDate={endDate || undefined} />
            <div className={styles.break} />
            <h2 className={styles.splitStatsHeader}>{currentFilterName} Statistics since: {stringDateToDate(initialDate).toLocaleDateString()}, until: {endDate ? stringDateToDate(endDate).toLocaleDateString() : 'today'}</h2>
            <div className={styles.break} />
            <Stack
              component="div"
              sx={{ margin: '0 auto' }}
              className={styles.splitStats}
              direction={{ xs: 'column', sm: 'column', md: 'column', lg: 'column', xl: 'row' }}
            >
              <StatTable allRuns={initialGameStats} runsList={gameStats} onClickHandler={statRowItemClickHandler} filterRuns={setCurrentFilter} type={'game'} />
              <StatTable allRuns={initialGameStats} runsList={gameOrderStats} onClickHandler={statRowItemClickHandler} filterRuns={setCurrentFilter} type={'game order'} />
            </Stack>
          </Stack >
          <div className={styles.break} />
          <Stack direction={{ xs: 'column', sm: 'column', md: 'column', lg: 'column', xl: 'row' }}>
            <Box flexGrow={1}>
              <div style={{ textAlign: 'center'}}>Succesful Runs per Game</div>
              <PieChart 
                series={[
                  { 
                    arcLabel: (item) => `${item.label} (${item.value})`,
                    arcLabelMinAngle: 30,
                    data: perGameData,
                    highlightScope: { faded: 'global', highlighted: 'item' },
                    highlighted: { additionalRadius: 5 },
                    faded: { additionalRadius: -5, color: 'gray' },
                  } 
                ]}
                colors={gamesColors}
                width={isMobile ? 350 : 600}
                height={isMobile ? 200 : 300}
                sx={{
                  [`& .${pieArcLabelClasses.root}`]: {
                    fill: 'white',
                    fontWeight: 'bold',
                  },
                }}
              />
            </Box>
            <Box flexGrow={1}>
              <div style={{ textAlign: 'center'}}>Succesful Runs per Game Order</div>
              <PieChart 
                series={[
                  { 
                    arcLabel: (item) => `${item.label} (${item.value})`,
                    arcLabelMinAngle: 30,
                    data: perGameOrderData,
                    highlightScope: { faded: 'global', highlighted: 'item' },
                    highlighted: { additionalRadius: 5 },
                    faded: { additionalRadius: -5, color: 'gray' },
                  } 
                ]}
                colors={gamesColors}
                width={isMobile ? 350 : 600}
                height={isMobile ? 200 : 300}
                sx={{
                  [`& .${pieArcLabelClasses.root}`]: {
                    fill: 'white',
                    fontWeight: 'bold',
                  },
                }}
              />
            </Box>
          </Stack>
          <div className={styles.break} />
          <h3 className={styles.splitStatsHeader}>Streak Stats</h3>
          <Stack direction={{ xs: 'column', sm: 'column', md: 'column', lg: 'column', xl: 'row' }}>
            <StreakTable runs={filteredRunsNoPartial} games={games} />
          </Stack>
          <div className={styles.break} />
          <h3 className={styles.splitStatsHeader}>Hits Stats</h3>
          <Stack
            component="div"
            sx={{ margin: '20px auto', maxWidth: 'xl' }}
            className={styles.splitStats}
            direction='column'
          >
            <Stack
              component="div"
              sx={{ margin: '0 auto' }}
              direction={{ xs: 'column', sm: 'column', md: 'column', lg: 'column', xl: 'row' }}
            >
            {games.map((game, index) => {
              const logo = logos[index];
              const totalRunsItem = totalRuns[index];
              const splits = game.splits;
              return (<GameTable key={index} onSplitHitClick={onSplitHitClick} game={game} logo={logo} totalRuns={totalRunsItem} splits={splits} />);
            })
            }
            </Stack>
          </Stack>
          {
            (selectedGameRuns.length > 0) ?
              <GameRunModal runs={selectedGameRuns} type='succeed' displayText={currentGameName} onClose={setSelectedGameRuns} />
              :
              ''
          }
          {(
            (splitHits.length > 0) ?
              <SplitHitModal runs={splitHits} type='hits' displayText={currentSplitName} onClose={setSplitHits} />
              :
              ''
          )}
        </div>
      </>
    );
  } else {
    return (
      <div className={styles.loading}>
        <CircularProgress />
      </div>
    );
  }
};
export default Statistics;
